import React, { useEffect, useMemo, useState } from 'react';
import { Button, DatePicker, Form, Select, Space, Table } from 'antd';
import { getBenefitSendRecordPageApi } from '@/api/member/member';

const { RangePicker } = DatePicker;

const benefitsTypeOptions = [
  { label: '全部', value: '' },
  { label: '入会礼', value: 'BIND' },
  { label: '首购礼', value: 'FIRST_ORDER' },
  { label: '升级礼', value: 'LEVEL_UP' },
  { label: '爱宠生日礼', value: 'PET_BIRTH' },
];

type Props = {
  shopId?: number | null;
  oneId: string;
  active: boolean;
};

const RightsGrantTab: React.FC<Props> = ({ shopId, oneId, active }) => {
  const [form] = Form.useForm();
  const [list, setList] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [total, setTotal] = useState(0);
  const [pageNum, setPageNum] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [initialized, setInitialized] = useState(false);

  useEffect(() => {
    form.resetFields();
    setList([]);
    setTotal(0);
    setPageNum(1);
    setPageSize(10);
    setInitialized(false);
  }, [shopId, oneId, form]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!active || !shopId || !oneId || initialized) return;
    handleSearch(1);
    setInitialized(true);
  }, [active, initialized, shopId, oneId]);

  const fetchBenefitsRecords = async (override?: Partial<{ pageNum: number; pageSize: number }>) => {
    if (!shopId || !oneId) return;
    const values = form.getFieldsValue();
    const range: any[] | undefined = values.sendTime;
    const params: any = {
      benefitsBasicType: values.benefitsType ?? '',
      oneId,
      startSendTime: range?.length ? range[0].format('YYYY-MM-DD HH:mm:ss') : '',
      endSendTime: range?.length ? range[1].format('YYYY-MM-DD HH:mm:ss') : '',
      pageNum: override?.pageNum || pageNum,
      pageSize: override?.pageSize || pageSize,
    };
    setLoading(true);
    try {
      const res = await getBenefitSendRecordPageApi(shopId, params);
      if (res?.code === 200) {
        setList(res.rows || res.data?.rows || []);
        setTotal(res.total || res.data?.total || 0);
        if (override?.pageNum) setPageNum(override.pageNum);
        if (override?.pageSize) setPageSize(override.pageSize);
      }
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (resetPage?: number) => {
    fetchBenefitsRecords({ pageNum: resetPage ?? 1 });
  };

  const handleReset = () => {
    form.resetFields();
    handleSearch(1);
  };

  const handlePageChange = (pn: number, ps: number) => {
    fetchBenefitsRecords({ pageNum: pn, pageSize: ps });
  };

  const columns = useMemo(
    () => [
      { title: '权益类型', dataIndex: 'benefitsName' },
      {
        title: '发放明细',
        dataIndex: 'benefitsSendValue',
        render: (v: string) => (v ? `${v}积分` : '—'),
      },
      {
        title: '会员等级',
        dataIndex: 'memberLeve',
        render: (v: string) => (v ? `${v}星会员` : '—'),
      },
      { title: '发放时间', dataIndex: 'sendTime' },
    ],
    [],
  );

  return (
    <div>
      <Form
        form={form}
        layout="inline"
        style={{ marginBottom: 12, rowGap: 12, columnGap: 12 }}>
        <Form.Item
          label="权益类型"
          name="benefitsType"
          initialValue="">
          <Select
            options={benefitsTypeOptions}
            style={{ width: 160 }}
          />
        </Form.Item>
        <Form.Item
          label="发放时间"
          name="sendTime">
          <RangePicker
            showTime
            format="YYYY-MM-DD HH:mm:ss"
          />
        </Form.Item>
        <Form.Item>
          <Space size={12}>
            <Button
              type="primary"
              onClick={() => handleSearch(1)}>
              查询
            </Button>
            <Button onClick={handleReset}>重置</Button>
          </Space>
        </Form.Item>
      </Form>

      <Table
        rowKey={(record: any, index) => record.id || `${record.oneId || 'one'}-${record.sendTime || index}-${index}`}
        loading={loading}
        columns={columns}
        dataSource={list}
        pagination={{
          current: pageNum,
          pageSize,
          total,
          showSizeChanger: true,
          showTotal: t => `共 ${t} 条`,
          onChange: (pn, ps) => handlePageChange(pn, ps),
        }}
      />
    </div>
  );
};

export default RightsGrantTab;
